import React from 'react';
import { MuiCard } from '@ace/ace-ui-web';
import { Typography, Box, Stack, ToggleButton } from '@mui/material';
import { BuildRounded } from '@mui/icons-material';
import { CardHeader } from '../card-header';
import { SaveButton } from '../save-button';
import { baseCardStyles } from '../common/cardStyles';

interface SkillsCardProps {
    title: string;
    subtitle: string;
    userSkills: string[];
    availableSkills: string[];
    skillsLoading: boolean;
    isEditable?: boolean;
    onSkillToggle?: (skill: string) => void;
    showSaveButton?: boolean;
    hasSkillChanges?: boolean;
    updating?: boolean;
    onSave?: () => void;
}

const SkillsCard: React.FC<SkillsCardProps> = ({
    title,
    subtitle,
    userSkills,
    availableSkills,
    skillsLoading,
    isEditable = false,
    onSkillToggle,
    showSaveButton = false,
    hasSkillChanges = false,
    updating = false,
    onSave,
}) => {
    const renderSkillsContent = () => {
        console.log('🔍 Available Skills Count:', availableSkills.length);
        console.log('🔍 Available Skills:', availableSkills);
        console.log('🔍 User Skills:', userSkills);
        
        if (skillsLoading) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                    Loading skills...
                </Typography>
            );
        }

        if (availableSkills.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                    No skills available for this site
                </Typography>
            );
        }

        return availableSkills.map((skill) => {
            const hasSkill = userSkills.includes(skill);
            return (
                <ToggleButton
                    key={skill}
                    value={skill}
                    selected={hasSkill}
                    disabled={!isEditable}
                    onClick={() => onSkillToggle?.(skill)}
                    sx={{
                        cursor: isEditable ? 'pointer' : 'default !important',
                        ...(hasSkill && {
                            backgroundColor: '#D40029 !important',
                            color: 'white !important',
                            borderColor: '#D40029 !important',
                            '&:hover': {
                                backgroundColor: isEditable ? '#AA001E !important' : '#D40029 !important',
                            }
                        }),
                        ...(!hasSkill && !isEditable && {
                            opacity: 0.5
                        })
                    }}
                >
                    {skill}
                </ToggleButton>
            );
        });
    };

    return (
        <MuiCard
            borderRadius={5}
            elevation={1}
            padding={2}
            variant="elevation"
            sx={baseCardStyles}
        >
            <CardHeader title={title} subtitle={subtitle} />

            <Box sx={{ mt: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <BuildRounded sx={{ height: 1, fontSize: 15, transform: 'scaleX(-1)', color: "gray" }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Skills</Typography>
                </Stack>
                <div className="toggle-group-skills">
                    {renderSkillsContent()}
                </div>
            </Box>

            {showSaveButton && onSave && (
                <SaveButton
                    label="Save Skills"
                    disabled={updating || !hasSkillChanges}
                    updating={updating}
                    onSave={onSave}
                />
            )}

            {!isEditable && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Your skills are managed by your supervisor. Contact them to request updates.
                    </Typography>
                </Box>
            )}
        </MuiCard>
    );
};

export default SkillsCard;